import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Root, Portal, Overlay, Content, Title, Close } from '@radix-ui/react-dialog';
import styles from './AnnouncementImage.module.scss';

/** Asks the viewer mounted in Layout to show a screenshot at full size. */
const OPEN_IMAGE_EVENT = 'announcements:image';

interface OpenImageDetail {
  src: string;
  alt: string;
}

interface AnnouncementImageProps {
  src: string;
  alt: string;
  /** The class of the small picture, which differs between the popup and the bell. */
  className?: string;
  /** In the editor's preview nothing opens. */
  isPreview?: boolean;
}

/**
 * A feature's screenshot inside an announcement (HAT-1489). It shows the whole screen with the new
 * element framed, so at the size of a message it tells the reader WHERE; pressing it shows the
 * screen full size, to read WHAT.
 */
export const AnnouncementImage = ({ src, alt, className, isPreview = false }: AnnouncementImageProps) => {
  const { t } = useTranslation();
  const picture = <img className={className} src={src} alt={alt} loading="lazy" />;
  if (isPreview) return picture;

  return (
    <button
      type="button"
      className={styles.enlarge}
      onClick={() => window.dispatchEvent(new CustomEvent<OpenImageDetail>(OPEN_IMAGE_EVENT, { detail: { src, alt } }))}
      aria-label={t('announcements.enlargeImage')}
      title={t('announcements.enlargeImage')}
    >
      {picture}
    </button>
  );
};

/**
 * The full-size view of an announcement's screenshot. One, in Layout, rather than one per picture: a
 * picture in the bell lives inside a popover that closes when anything outside it is clicked, and a
 * viewer mounted in there would close with it.
 *
 * A click anywhere closes it, the picture included, like any photo viewer; so do Escape and «סגור».
 * The viewer covers the whole window, so to Radix every click on it lands INSIDE the viewer and never
 * dismisses it on its own - hence the explicit click handler. A dialog underneath (a flag's release
 * dialog) stays open: the viewer is the top layer, and only it hears the click.
 */
export const AnnouncementImageViewer = () => {
  const { t } = useTranslation();
  const [image, setImage] = useState<OpenImageDetail | null>(null);

  useEffect(() => {
    const onOpen = (event: Event) => setImage((event as CustomEvent<OpenImageDetail>).detail ?? null);
    window.addEventListener(OPEN_IMAGE_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_IMAGE_EVENT, onOpen);
  }, []);

  return (
    <Root open={!!image} onOpenChange={(open) => !open && setImage(null)}>
      <Portal>
        <Overlay className={styles.overlay} data-announcement-image-viewer="" />
        <Content className={styles.viewer} data-announcement-image-viewer="" aria-describedby={undefined} onClick={() => setImage(null)}>
          <Title className={styles.hiddenTitle}>{image?.alt || t('announcements.enlargeImage')}</Title>
          {image && <img className={styles.full} src={image.src} alt={image.alt} />}
          <Close className={`${styles.close} body-sm-semibold`}>{t('close')}</Close>
        </Content>
      </Portal>
    </Root>
  );
};
